import { AddAPhoto } from '@mui/icons-material';
import {
  getEmbedPlugin,
  getImagePlugin,
  getMultimediaSearchPlugin,
  getVideoPlugin,
  ItemType,
  MediaTypesEnum,
  NotificationTypeEnum,
  PushNotificationFn,
  UploadProgress,
  UploadStatesEnum,
  useNotification,
} from '@prismamedia/one-components';
import Axios from 'axios';
import { startsWith as _startsWith, when as _when } from 'ramda';
import React, { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useParams } from 'react-router-dom';
import { SetterOrUpdater } from 'recoil';
import {
  ArticleFormat,
  BrandKey,
  SearchArticles_searchArticles,
} from '../../__generated__/queries-web';
import { useDailymotionUpdate } from '../../apollo/mutations/dailymotion.web.graphql';
import { useGetArticle } from '../../apollo/queries/articles.web.graphql';
import { useDailymotionUploadURLGetter } from '../../apollo/queries/dailymotion.web.graphql';
import { useSuggestArticleQuery } from '../../apollo/queries/suggestArticles.web.graphql';
import { history } from '../../history';
import { brandsOptions } from '../../utils/brands';
import { useGlobalThumbnail } from '../../utils/globalState';
import {
  getDefaultDailymotionMedia,
  getFirstMedia,
  MediaFormat,
  parseMedias,
} from '../../utils/media';
import { sleep } from '../../utils/sleep';
import { KeyOfEnum } from '../../utils/typescriptUtils';
import {
  embedImage,
  uploadAndEmbedImage,
  uploadImage,
} from '../../utils/upload';
import { isPage } from '../../utils/url';
import { AdvancedSearch } from '../AppBar/AdvancedSearch';
import { searchByPluginType } from '../Draft/plugins/shared/searchArticleForMedia';
import {
  ARTICLE_FORMAT,
  BRAND_FORMAT,
  DEFAULT_MAX_IMAGE_SIZE,
  EMPTY_FORMAT,
  FB_FORMAT,
  FLAG_FORMAT,
  ILLUSTRATION_FORMAT,
  LOGO_FORMAT,
  MAX_HEIGHT,
  MAX_WIDTH,
  MEDIA_TYPES,
  MIN_HEIGHT,
  MIN_WIDTH,
  PINTEREST_FORMAT,
  THUMBNAIL_ERROR,
  THUMBNAIL_MAX_HEIGHT,
  THUMBNAIL_MAX_SIZE,
  THUMBNAIL_MAX_WIDTH,
  THUMBNAIL_MIN_HEIGHT,
  THUMBNAIL_MIN_WIDTH,
  THUMBNAIL_UPLOADED,
} from './constants';
import {
  getAllowedMedias,
  getItemToIframely,
  getItemToRender,
} from './plugins-utils';

enum FormatType {
  Cerise = 'Cerise',
  Page = 'Page',
  Default = 'Default',
}

export const postImages = (brandKey: string) => async (files: File[]) => {
  const iframelys = [];
  for (let i = 0; i < files.length; i++) {
    const iframely = await uploadAndEmbedImage(files[i], brandKey, {
      withFileName: true,
    });
    iframely && iframelys.push(iframely);
  }
  return iframelys;
};

const isImageMinPixels = (imageUrl: string, type: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      const width = type === 'width' && img.width;
      const height = type === 'height' && img.height;
      resolve((width && width < MIN_WIDTH) || (height && height < MIN_HEIGHT));
    };

    img.onerror = (error) => {
      reject(error);
    };
  });
};

const isImageMaxPixels = (imageUrl: string, type: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      const width = type === 'width' && img.width;
      const height = type === 'height' && img.height;
      resolve((width && width > MAX_WIDTH) || (height && height > MAX_HEIGHT));
    };

    img.onerror = (error) => {
      reject(error);
    };
  });
};

const handleThumbnail = async (
  file: File,
  imageUrl: string,
  pushNotification: PushNotificationFn,
) => {
  if (file.size > DEFAULT_MAX_IMAGE_SIZE) {
    pushNotification({
      message: THUMBNAIL_MAX_SIZE(file.name),
      type: NotificationTypeEnum.error,
    });
    return NotificationTypeEnum.error;
  } else if (await isImageMinPixels(imageUrl, 'width')) {
    pushNotification({
      message: THUMBNAIL_MIN_WIDTH(file.name),
      type: NotificationTypeEnum.error,
    });
    return NotificationTypeEnum.error;
  } else if (await isImageMinPixels(imageUrl, 'height')) {
    pushNotification({
      message: THUMBNAIL_MIN_HEIGHT(file.name),
      type: NotificationTypeEnum.error,
    });
    return NotificationTypeEnum.error;
  } else if (await isImageMaxPixels(imageUrl, 'width')) {
    pushNotification({
      message: THUMBNAIL_MAX_WIDTH(file.name),
      type: NotificationTypeEnum.error,
    });
    return NotificationTypeEnum.error;
  } else if (await isImageMaxPixels(imageUrl, 'height')) {
    pushNotification({
      message: THUMBNAIL_MAX_HEIGHT(file.name),
      type: NotificationTypeEnum.error,
    });
    return NotificationTypeEnum.error;
  }
};

const postThumbnail = (
  brandKey: string,
  updateThumbnail: any,
  pushNotification: PushNotificationFn,
  videoId?: string,
  setThumbnail?: SetterOrUpdater<string | undefined>,
) => async (files: File[]) => {
  const imageUrl = await uploadImage(files[0], brandKey);
  const iframely = await uploadAndEmbedImage(files[0], brandKey, {
    withFileName: true,
  });
  const handler = await handleThumbnail(
    files[0],
    imageUrl.url,
    pushNotification,
  );

  if (handler === NotificationTypeEnum.error) {
    return [];
  }

  const result = await updateThumbnail({
    variables: {
      brandKey: brandKey as BrandKey,
      thumbnailUrl: imageUrl.url,
      videoId: videoId || '',
    },
  }).catch(() => {
    pushNotification({
      message: THUMBNAIL_ERROR,
      type: NotificationTypeEnum.error,
    });
  });
  if (result?.data?.dailymotionUpdate) {
    setThumbnail?.(imageUrl.url);
    pushNotification({
      message: THUMBNAIL_UPLOADED,
      type: NotificationTypeEnum.success,
    });
  }

  return [iframely];
};
const getUrlForDiapo = (domain: string, path: string) => {
  const removeSlash = _when(_startsWith('/'), (data) => data.slice(1));
  path = removeSlash(path);
  domain = removeSlash(domain);
  return `https://${domain}/${path}`;
};

export const embedMediaFromArticle = async (
  article: {
    isSuggested: boolean;
  } & Pick<SearchArticles_searchArticles, 'articleUrls' | 'format' | 'medias'>,
  articleFormat: ArticleFormat,
) => {
  let mediaUrl = '';

  if (articleFormat === ArticleFormat.Slideshow) {
    const { domain, path } = article.articleUrls[0].url;
    mediaUrl = getUrlForDiapo(domain, path);
  } else if (
    [ArticleFormat.Video, ArticleFormat.Rich].includes(articleFormat)
  ) {
    const media = getFirstMedia(parseMedias(article.medias), MediaFormat.Video);
    if (media) {
      mediaUrl = media.iframely.meta.canonical;
    }
  }

  if (mediaUrl) {
    if (article.isSuggested) {
      ReactGA.event({
        category: 'Article Edit',
        action: `Suggestion ${article.format}`,
        label: window.location.href,
      });
    }

    return embedImage(mediaUrl);
  }
};

export const usePlugins = (
  allowedMedia: MEDIA_TYPES[],
  brandKey: BrandKey,
  subjectId?: string,
  videoId?: string,
  options?: {
    allowsMultipleImgs?: boolean;
    disableImgWidth?: boolean;
  },
) => {
  const { pushNotification } = useNotification();
  const allowedMedias = useMemo(
    () =>
      getAllowedMedias(allowedMedia, {
        subjectId,
      }),
    [allowedMedia, subjectId],
  );

  const { id } = useParams();
  const { data: dataArticles } = useGetArticle(id);
  const { suggestQuery } = useSuggestArticleQuery(dataArticles);

  const getDailymotionUploadURL = useDailymotionUploadURLGetter();
  const [updateThumbnail] = useDailymotionUpdate();
  const [, setThumbnail] = useGlobalThumbnail();

  const availablePlugins: KeyOfEnum<MEDIA_TYPES, any> = useMemo(
    () => ({
      [MEDIA_TYPES.IMAGE]: getImagePlugin({
        allowsMultipleImgs: options?.allowsMultipleImgs,
        disableImgWidth: options?.disableImgWidth,
        postImages: postImages(brandKey),
        pushNotification,
      }),
      [MEDIA_TYPES.EMBED]: getEmbedPlugin({ getIframely: embedImage }),
      [MEDIA_TYPES.DAILYMOTION]: getVideoPlugin({
        postVideo: async (
          localFile: File,
          setProgress: React.Dispatch<React.SetStateAction<UploadProgress[]>>,
        ) => {
          const cancelTokenSource = Axios.CancelToken.source();
          const uploadURLs = await getDailymotionUploadURL(
            { brandKey, count: 1 },
            { fetchPolicy: 'network-only' },
          );

          const handleProgess = async (
            name: string,
            size: number,
            state: UploadStatesEnum,
            url: string,
            hostName: string,
          ) => {
            await sleep(1000);
            const response = await Axios.get(`${url}`);

            setProgress((previousState) => {
              const result = [...previousState];
              const target = result.find((item) => item.name === name);
              if (target) {
                if (hostName === MediaTypesEnum.DAILYMOTION_TYPE) {
                  switch (response.data.state) {
                    case 'uploading':
                      target.state = UploadStatesEnum.UPLOADING;
                      target.received = response.data.received;
                      break;
                    case 'done':
                      target.state = UploadStatesEnum.DONE;
                      target.received = response.data.received;
                      break;
                    default:
                      target.state = UploadStatesEnum.INTERRUPTED;
                      break;
                  }
                }
                if (
                  target.received < size &&
                  state === UploadStatesEnum.UPLOADING
                ) {
                  handleProgess(
                    target.name,
                    target.size,
                    target.state,
                    url,
                    target.host,
                  );
                }
              } else {
                cancelTokenSource.cancel();
              }
              return result;
            });
          };

          const handleUpload = async (
            file: File,
            uploadURL: string,
            progressURL: string,
          ) => {
            const process = {
              name: file.name,
              size: file.size,
              received: 0,
              url: progressURL,
              host: MediaTypesEnum.DAILYMOTION_TYPE,
              state: UploadStatesEnum.UPLOADING,
            };

            setProgress((previous) => [...previous, process]);
            handleProgess(
              process.name,
              process.size,
              process.state,
              process.url,
              process.host,
            );

            const data = new FormData();
            data.append('file', file);
            try {
              const response = await Axios.post(`${uploadURL}`, data, {
                cancelToken: cancelTokenSource.token,
              });
              return response;
            } catch (error) {
              setProgress((previousState) => {
                const result = [...previousState];
                const target = result.find((item) => item.name === file.name);
                if (target) {
                  target.state = UploadStatesEnum.INTERRUPTED;
                }
                return result;
              });
              throw { message: 'Le chargement a été interrompu' };
            }
          };

          const response = await handleUpload(
            localFile,
            uploadURLs.dailymotionUploadURL[0].uploadURL,
            uploadURLs.dailymotionUploadURL[0].progressURL,
          );

          return getDefaultDailymotionMedia(
            response?.data.name,
            response?.data.url,
          );
        },
        pushNotification,
      }),
      [MEDIA_TYPES.THUMBNAIL]: videoId
        ? getImagePlugin({
            allowsMultipleImgs: false,
            disableImgWidth: true,
            postImages: postThumbnail(
              brandKey,
              updateThumbnail,
              pushNotification,
              videoId,
              setThumbnail,
            ),
            pushNotification,
            icon: <AddAPhoto />,
            tooltip: 'Thumbnail',
          })
        : undefined,
      [MEDIA_TYPES.ARTICLE_VIDEO ||
      MEDIA_TYPES.SLIDESHOW ||
      MEDIA_TYPES.PODCAST ||
      MEDIA_TYPES.FOTOWEB]: getMultimediaSearchPlugin({
        allowedMedias,
        suggestArticlesFn: suggestQuery,
        searchFn: async (searchParams, opts, pluginType) =>
          await searchByPluginType({
            searchParams,
            pluginType,
            opts,
            brandKey,
            searchSchedule: isPage(history.location.pathname),
          }),
        advancedSearch: brandKey &&
          dataArticles?.article?.format && {
            component: AdvancedSearch,
            props: { brandKey, articleFormat: dataArticles.article.format },
          },
        itemToIframely: async (item: ItemType<any>, pluginType: string) => {
          return (await getItemToIframely(item, pluginType))!;
        },
        itemToRender: (item: ItemType<any>, pluginType?: string) => {
          return getItemToRender(brandKey, item, pluginType);
        },
        itemClickCallback: (item) => {
          if (item.isSuggested) {
            ReactGA.event({
              category: 'Article Edit',
              action: `Suggestion ${item.format}`,
              label: window.location.href,
            });
          }
        },
      }),
    }),
    // eslint-disable-next-line
    [
      allowedMedias,
      brandKey,
      getDailymotionUploadURL,
      options?.allowsMultipleImgs,
      options?.disableImgWidth,
      videoId,
      dataArticles,
    ],
  );

  return useMemo(
    () =>
      allowedMedia
        .filter((media: MEDIA_TYPES) => availablePlugins[media])
        .map((media: MEDIA_TYPES) => availablePlugins[media]),
    [allowedMedia, availablePlugins],
  );
};

export const useFormats = (brandKey: BrandKey) => {
  const formatType = brandsOptions[brandKey].isCerise
    ? FormatType.Cerise
    : isPage(history.location.pathname)
    ? FormatType.Page
    : FormatType.Default;

  return useMemo(() => {
    return {
      Cerise: [
        {
          label: ARTICLE_FORMAT,
          value: ARTICLE_FORMAT.toLowerCase(),
        },
        {
          label: FB_FORMAT,
          value: FB_FORMAT.toLowerCase(),
        },
        {
          label: PINTEREST_FORMAT,
          value: PINTEREST_FORMAT.toLowerCase(),
        },
      ],
      Page: [
        {
          label: EMPTY_FORMAT,
          value: ' ',
        },
        {
          label: ILLUSTRATION_FORMAT,
          value: 'illustration',
        },
        {
          label: LOGO_FORMAT,
          value: 'logo',
        },
        {
          label: FLAG_FORMAT,
          value: 'banner',
        },
        {
          label: BRAND_FORMAT,
          value: 'brand',
        },
      ],
      Default: undefined,
    }[formatType];
  }, [formatType]);
};
